import { createApi } from '@reduxjs/toolkit/query/react'
import customFetchBase from '../services/CustomFetchBase'

/**
 * API slice for handling product-related operations.
 */
export const productsApi = createApi({
    reducerPath: 'productsApi',
    baseQuery: customFetchBase,
    endpoints: (builder) => ({
        /**
         * Mutation to fetch a list of products with optional filtering and pagination.
         *
         * @param {Object} params - Query parameters for fetching products.
         * @param {number} params.page - The page number for pagination.
         * @param {number} params.limit - The number of items per page.
         * @param {boolean} params.ItemLatest - Whether to fetch the latest items.
         * @param {string} [params.sortBy] - Sorting criteria (e.g., "name", "date").
         * @param {string} [params.ItemName] - Filter products by name.
         * @param {string} [params.ItemVersion] - Filter products by version.
         * @param {string} [params.ItemPlatform] - Filter products by platform.
         * @returns {Promise<any>} The response containing the list of products.
         */
        getProducts: builder.mutation({
            query: ({
                page,
                limit,
                ItemLatest,
                sortBy,
                ItemName,
                ItemVersion,
                ItemPlatform
            }: {
                page: any
                limit: any
                ItemLatest: any
                sortBy?: string
                ItemName?: string
                ItemVersion?: string
                ItemPlatform?: string
            }) => {
                return {
                    url: `/products?${page ? `page=${page}` : ''}${limit ? `&limit=${limit}` : ''}${
                        ItemLatest ? `&ItemLatest=${ItemLatest}` : ''
                    }${sortBy ? `&sortBy=${sortBy}` : ''}${ItemName ? `&ItemName=${ItemName}` : ''}${
                        ItemVersion ? `&ItemVersion=${ItemVersion}` : ''
                    }${ItemPlatform ? `&ItemPlatform=${ItemPlatform}` : ''}`,
                    method: 'GET'
                }
            },
            onQueryStarted: async (args, { queryFulfilled }) => {
                try {
                    await queryFulfilled
                } catch (error) {}
            }
        }),

        /**
         * Mutation to generate a presigned URL for downloading a product.
         *
         * @param {Object} params - Query parameters for downloading a product.
         * @param {string} params.path - The file path of the product.
         * @param {string} params.name - The name of the product.
         * @param {string} params.description - The product description.
         * @param {string} params.platform - The platform (e.g., Windows, Mac).
         * @param {string} params.version - The product version.
         * @param {string} params.fileType - The file type (e.g., zip, exe).
         * @param {string} [params.fileName] - The actual filename for download.
         * @returns {Promise<any>} The response containing the presigned URL.
         */
        downloadProducts: builder.mutation({
            query: ({
                path,
                name,
                description,
                platform,
                version,
                fileType,
                fileName
            }: {
                path: string
                name: string
                description: string
                platform: string
                version: string
                fileType: string
                fileName?: string
            }) => {
                return {
                    url: `/presigned?${path ? `path=${path}` : ''}${name ? `&name=${name}` : ''}${
                        description ? `&description=${description}` : ''
                    }${platform ? `&platform=${platform}` : ''}${version ? `&version=${version}` : ''}${
                        fileType ? `&fileType=${fileType}` : ''
                    }${fileName ? `&fileName=${fileName}` : ''}`,
                    method: 'GET'
                }
            },
            onQueryStarted: async (args, { queryFulfilled }) => {
                try {
                    await queryFulfilled
                } catch (error) {}
            }
        })
    })
})

/**
 * Auto-generated hooks for the defined mutations.
 */
export const { useGetProductsMutation, useDownloadProductsMutation } = productsApi
